/**
 * HTTP请求拦截器 - 自动处理token刷新
 * 确保token失效后只刷新一次，刷新成功后回调所有等待的请求
 */
import axios from 'axios';
import pub from '../service/public';
import cookie from '../service/cookie';
import config from '../../static/config.json';
import router from '../router';

// 创建axios实例
const httpClient = axios.create();

// 请求拦截器
httpClient.interceptors.request.use(
  (config) => {
    // 只有非排除的URL才添加token
    if (!isUrlExcluded(config.url || '')) {
      const token = cookie.getCookie(pub.token.token_key);
      if (token) {
        config.headers.Authorization = token;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
httpClient.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;

    // 检查是否是token相关的业务错误码
    if (isTokenExpiredBusinessCode(response) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // 等待token刷新完成
        await waitForTokenRefresh();

        // 更新请求头中的token
        const newToken = cookie.getCookie(pub.token.token_key);
        if (newToken) {
          originalRequest.headers.Authorization = newToken;
        }

        // 重新发送原始请求
        return httpClient(originalRequest);
      } catch (refreshError) {
        // token刷新失败，跳转到登录页
        console.error('Token refresh failed:', refreshError);
        toLogin();
        return Promise.reject(new Error('Token refresh failed'));
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 检查是否是HTTP状态码token错误
    if (isHttpStatusTokenError(error) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // 等待token刷新完成
        await waitForTokenRefresh();

        // 更新请求头中的token
        const newToken = cookie.getCookie(pub.token.token_key);
        if (newToken) {
          originalRequest.headers.Authorization = newToken;
        }

        // 重新发送原始请求
        return httpClient(originalRequest);
      } catch (refreshError) {
        // token刷新失败，跳转到登录页
        console.error('Token refresh failed:', refreshError);
        toLogin();
        return Promise.reject(error);
      }
    }
    // 非 token 相关的 HTTP 错误：在这里统一处理 404 / 500
    if (error.response) {
      const status = error.response.status;
      if (status === 404) {
        const normalized = {
          ...error.response,
          data: {
            code: 404,
            message: '接口未定义，请联系相关人员'
          }
        };
        pub.tip(normalized.data.message);
        // 将规范化后的响应作为成功结果返回，避免业务层进入 catch
        return Promise.resolve(normalized);
      }
      if (status === 500) {
        const normalized = {
          ...error.response,
          data: {
            code: 500,
            message: '服务器错误，请联系相关人员'
          }
        };
        pub.tip(normalized.data.message);
        return Promise.resolve(normalized);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * 检查URL是否被排除在token验证之外
 */
function isUrlExcluded(url) {
  const excludedUrls = [
    '/Usr/UsrLogin'  // 登录
  ];
  return excludedUrls.some(excludedUrl => url.includes(excludedUrl));
}

/**
 * 检查是否是HTTP状态码token错误
 */
function isHttpStatusTokenError(error) {
  if (!error.response) return false;

  const status = error.response.status;
  const originalRequest = error.config;

  // 检查URL是否被排除
  if (originalRequest && originalRequest.url && isUrlExcluded(originalRequest.url)) {
    return false;
  }

  // HTTP状态码相关
  return status === 401 || status === 403;
}

/**
 * 检查是否是业务错误码token过期
 */
function isTokenExpiredBusinessCode(response) {
  if (!response || !response.data || !response.config) return false;

  const data = response.data;
  const originalRequest = response.config;

  // 检查URL是否被排除
  if (originalRequest.url && isUrlExcluded(originalRequest.url)) {
    return false;
  }

  // 排除token刷新接口本身，避免无限循环
  if (originalRequest.url && originalRequest.url.includes('/Usr/RefreshToken')) {
    return false;
  }

  // 业务状态码相关
  if (data && data.code) {
    // 仅当 code 为全局定义的 TOKEN_TIMEOUT(1283) 时视作 token 过期
    return data.code === pub.backCode.TOKEN_TIMEOUT;
  }

  return false;
}

/**
 * 跳转到登录页
 */
function toLogin() {
  pub.tip('登录信息已失效，请重新登录');
  if (router.currentRoute.fullPath === "/login") {
    router.replace({ path: '/login' });
  } else {
    router.replace({
      path: '/login',
      query: { redirect: router.currentRoute.fullPath }
    });
  }
}

/**
 * 等待token刷新完成
 */
function waitForTokenRefresh() {
  return new Promise((resolve, reject) => {
    // 初始化 tokenRefreshState（如果不存在）
    if (!pub.tokenRefreshState) {
      pub.tokenRefreshState = {
        isRefreshing: false,
        refreshPromise: null,
        waitingRequests: []
      };
    }

    // 如果正在刷新，等待刷新完成
    if (pub.tokenRefreshState.isRefreshing) {
      // 等待现有的刷新Promise完成
      if (pub.tokenRefreshState.refreshPromise) {
        pub.tokenRefreshState.refreshPromise
          .then(() => resolve())
          .catch((error) => reject(error));
      } else {
        // 如果没有refreshPromise，说明刷新状态异常，直接拒绝
        reject(new Error('Token refresh state error'));
      }
    } else {
      // 开始刷新token
      pub.tokenRefreshState.isRefreshing = true;

      let refresh_token = cookie.getCookie(pub.token.refresh_key);
      cookie.setCookie(pub.token.token_key, refresh_token);

      pub.tokenRefreshState.refreshPromise = axios.post(config.BACK_API_ROOT + '/Usr/RefreshToken', {}, {
        headers: {
          'Authorization': cookie.getCookie(pub.token.token_key)
        }
      });

      pub.tokenRefreshState.refreshPromise
        .then(function (res) {
          if (res.data.code === pub.backCode.SUCCESS) {
            // 刷新成功，更新token
            if (res.data.data && res.data.data.authorization) {
              cookie.setCookie(pub.token.token_key, res.data.data.authorization);
            }

            // 执行所有等待队列中的请求回调
            if (pub.tokenRefreshState.waitingRequests) {
              pub.tokenRefreshState.waitingRequests.forEach(waitingRequest => {
                if (waitingRequest.callBack) {
                  waitingRequest.callBack(waitingRequest.param);
                }
                if (waitingRequest.resolve) {
                  waitingRequest.resolve();
                }
              });
            }

            // 清空等待队列
            pub.tokenRefreshState.waitingRequests = [];

            resolve();
          } else {
            // 刷新失败，跳转登录页
            reject(new Error('Token refresh failed'));
          }
        })
        .catch(function (error) {
          console.log('Token refresh error:', error);
          reject(error);
        })
        .finally(() => {
          // 重置刷新状态
          pub.tokenRefreshState.isRefreshing = false;
          pub.tokenRefreshState.refreshPromise = null;
          pub.tokenRefreshState.waitingRequests = [];
        });
    }
  });
}

export default httpClient;



// WEBPACK FOOTER //
// ./src/request/requestInterceptor.js